// hooks/useAdminDashboardLogic.js
import { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import adminApi from 'services/api/adminApi';
import { fetchEmployees } from 'store/employeeSlice';

const useAdminDashboardLogic = () => {
  const dispatch = useDispatch();
  const [currentTime, setCurrentTime] = useState(new Date());
  const [isLoading, setIsLoading] = useState(false);
  const [dashboardSummary, setDashboardSummary] = useState({});
  const [strangers, setStrangers] = useState([]);
  const [attendanceRecords, setAttendanceRecords] = useState([]);
  const [filteredRecords, setFilteredRecords] = useState([]);
  const [summary, setSummary] = useState({});
  const [stats, setStats] = useState({
    totalDetections: 0,
    newToday: 0,
  });

  // Update current time every second
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  // Fetch employees on component mount
  useEffect(() => {
    dispatch(fetchEmployees());
  }, [dispatch]);

  // Fetch dashboard summary and strangers on component mount
  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setIsLoading(true);
        const [summaryData, employeesData] = await Promise.all([
          adminApi.getDashboardSummary(),
          adminApi.getEmployees()
        ]);
        
        setDashboardSummary(summaryData);
        
        // Process strangers data
        const strangerData = employeesData
          .filter(emp => emp?.isUnkown)
          .map(emp => ({
            id: emp?.employee_id || `STR-${emp?.id}`,
            first_name: emp?.first_name,
            email: emp?.email,
            department: emp?.department,
            date_of_join: emp?.date_of_join,
            last_name: emp?.last_name,
            imageUrl: emp?.image1 || 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400&h=400&fit=crop&crop=face',
            location: emp?.cameraName || 'Unknown',
            cameraName: emp?.cameraName || 'CAM-000',
            confidence: emp?.confidence || 85,
            detectedAt: emp?.detectedAt ? new Date(emp.detectedAt) : new Date(),
            status: emp?.status || 'new',
            detectionCount: emp?.detection_count || 1,
            notes: emp?.notes || '',
            originalData: emp
          }));

        setStrangers(strangerData);

        // Calculate stats
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const newToday = strangerData.filter(stranger => {
          const detectedDate = new Date(stranger.detectedAt);
          detectedDate.setHours(0, 0, 0, 0);
          return detectedDate.getTime() === today.getTime();
        }).length;

        setStats({
          totalDetections: strangerData.length,
          newToday: newToday
        });
      } catch (err) {
        console.error("Error fetching dashboard data:", err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  // Fetch attendance data
  const fetchAttendance = async () => {
    try {
      setIsLoading(true);
      const data = await adminApi.getAttendance();
      
      // Convert checkIn/checkOut strings to Date objects
      const records = data?.records
        .filter((r) => r.employee.isUnkown == false)
        ?.map((r) => ({
          ...r,
          checkIn: r.checkIn ? new Date(r.checkIn) : null,
          checkOut: r.checkOut ? new Date(r.checkOut) : null,
        })) || [];
      
      setSummary(data?.summary || {});
      setAttendanceRecords(records);
      setFilteredRecords(records);
    } catch (err) {
      console.error("Error fetching attendance:", err);
    } finally {
      setIsLoading(false);
    }
  };

  // Format date and time
  const formatDateTime = (date) => {
    return date?.toLocaleString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true
    });
  };

  return {
    // State
    currentTime,
    isLoading,
    dashboardSummary,
    strangers,
    attendanceRecords,
    filteredRecords,
    summary,
    stats,
    
    // Functions
    fetchAttendance,
    formatDateTime
  };
};

export default useAdminDashboardLogic;